import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/authContext';
import { proposalProjectApiConnect } from '../../../lib/api/proposalProjectApi.connect';
/**
 * Dev-only "Resync from MinIO" banner for the V2 shell.
 *
 * Mirrors the legacy ProposalDashboard banner: re-runs the MinIO → DB sync
 * without re-extracting, then invalidates every relevant React Query cache
 * so the V2 panes pull fresh data. Hidden in production builds.
 */ export function DevResyncBanner() {
    const { id } = useParams();
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const [resyncing, setResyncing] = useState(false);
    const handleResync = useCallback(async ()=>{
        if (!id || resyncing) return;
        setResyncing(true);
        try {
            const result = await proposalProjectApiConnect.resyncExtractedData(id, user?.tenant_id || '');
            toast.success(`Resynced: ${result.clinsSynced} CLINs, ${result.lcatsSynced} LCATs, ${result.requirementsSynced} requirements, ${result.classificationsSynced} classifications`);
            // Invalidate every cache the V2 shell consumes.
            queryClient.invalidateQueries({
                queryKey: [
                    'proposal-project',
                    id
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'project',
                    id
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'projectFiles',
                    id
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'clins'
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'lcats'
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'complianceMatrix'
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'shredView'
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'solicitationInsights',
                    id
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'projectVolumes',
                    'list',
                    id
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    'projectActivity',
                    'list',
                    id
                ]
            });
        } catch (err) {
            toast.error(`Resync failed: ${err instanceof Error ? err.message : String(err)}`);
        } finally{
            setResyncing(false);
        }
    }, [
        id,
        resyncing,
        user?.tenant_id,
        queryClient
    ]);
    if (process.env.NODE_ENV !== 'development') return null;
    if (!id) return null;
    // Compact floating badge anchored to the top of the content column (parent
    // is `relative`), rather than a full-width bar that pushes layout and
    // collides with the sidebar. Dev-only, so it never ships.
    return /*#__PURE__*/ _jsx("div", {
        className: "pointer-events-none absolute inset-x-0 top-2 z-30 flex justify-center",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "pointer-events-auto flex items-center gap-1.5 rounded-full border border-yellow-300 bg-yellow-50 py-1 pl-2.5 pr-1.5 shadow-sm",
            children: [
                /*#__PURE__*/ _jsx("span", {
                    className: "text-[10px] font-semibold uppercase tracking-wide text-yellow-700",
                    children: "Dev"
                }),
                /*#__PURE__*/ _jsxs("button", {
                    type: "button",
                    onClick: handleResync,
                    disabled: resyncing,
                    title: "Resync from MinIO",
                    className: "flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium text-yellow-800 transition-colors hover:bg-yellow-100 disabled:opacity-60",
                    children: [
                        /*#__PURE__*/ _jsx(RefreshCw, {
                            className: `h-3 w-3 ${resyncing ? 'animate-spin' : ''}`
                        }),
                        resyncing ? 'Resyncing…' : 'Resync'
                    ]
                })
            ]
        })
    });
}
